import { displayDateUS } from "../../utils/dateUtils";
import { RN_PROTOCOL_ITEMS } from "../../components/RN/rnProtocolConstants";
import { LVN_PROTOCOL_ITEMS } from "../../components/Lvn/lvnProtocolConstants";
import { HHA_PROTOCOL_ITEMS } from "../../components/HHA/hhaProtocolConstants";
import { PT_PROTOCOL_ITEMS } from "../../components/PT/ptProtocolConstants";
import { PTA_PROTOCOL_ITEMS } from "../../components/PTA/ptaProtocolConstants";
import { OT_PROTOCOL_ITEMS } from "../../components/OT/otProtocolConstants";
import { OTA_PROTOCOL_ITEMS } from "../../components/OTA/otaProtocolConstants";
import { MSW_PROTOCOL_ITEMS } from "../../components/MSW/mswProtocolConstants";
import { ST_PROTOCOL_ITEMS } from "../../components/St/stProtocolConstants";

/** @param {unknown} value */
export function escapeHtml(value) {
  if (value == null || value === "") return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

export function formatDate(val) {
  if (!val) return "";
  const s = displayDateUS(val);
  return s || String(val);
}

/**
 * @param {{ status?: number, result?: unknown }} response
 * @param {string|number|undefined} targetUserId
 */
export function parseJobDescriptionFromApiResponse(response, targetUserId) {
  if (!response || response.status !== 1) return null;
  const { result } = response;
  if (Array.isArray(result)) {
    return result.length > 0 ? result[0] : null;
  }
  if (result?.jobDescription) {
    return result.jobDescription;
  }
  if (result?.data && typeof result.data === "object") {
    return result.data;
  }
  if (result?.rolesAndResponsibilities) {
    return result.rolesAndResponsibilities;
  }
  if (result?.items && Array.isArray(result.items)) {
    const uid = parseInt(String(targetUserId), 10);
    return result.items.find((item) => item.userId === uid) || null;
  }
  if (
    result &&
    typeof result === "object" &&
    !Array.isArray(result) &&
    (result.id != null ||
      result.role != null ||
      result.name != null ||
      result.userId != null)
  ) {
    return result;
  }
  return result || null;
}

/** @type {Record<string, unknown[]>} */
const ROLE_PROTOCOL_ITEMS = {
  RN: RN_PROTOCOL_ITEMS,
  LVN: LVN_PROTOCOL_ITEMS,
  HHA: HHA_PROTOCOL_ITEMS,
  PT: PT_PROTOCOL_ITEMS,
  PTA: PTA_PROTOCOL_ITEMS,
  OT: OT_PROTOCOL_ITEMS,
  OTA: OTA_PROTOCOL_ITEMS,
  MSW: MSW_PROTOCOL_ITEMS,
  ST: ST_PROTOCOL_ITEMS,
};

function normalizeJobDescriptionRole(role) {
  const r = String(role ?? "")
    .trim()
    .toUpperCase();
  if (ROLE_PROTOCOL_ITEMS[r]) return r;
  if (r === "LICENSEDVOCATIONALNURSE" || r === "LPN") return "LVN";
  if (r === "REGISTEREDNURSE" || r === "REGISTERED NURSE") return "RN";
  if (r === "PHYSICALTHERAPIST") return "PT";
  if (r === "PHYSICALTHERAPISTASSISTANT") return "PTA";
  if (r === "OCCUPATIONALTHERAPIST") return "OT";
  if (r === "CERTIFIEDOCCUPATIONALTHERAPYASSISTANT" || r === "OCCUPATIONALTHERAPYASSISTANT")
    return "OTA";
  if (r === "MEDICALSOCIALWORKER" || r === "SOCIALWORKER") return "MSW";
  if (r === "SPEECHTHERAPIST" || r === "SLP") return "ST";
  return "RN";
}

function reportsToDisplay(item, seniorStaffNurse) {
  const fromTenant = String(seniorStaffNurse || "").trim();
  if (fromTenant) return escapeHtml(fromTenant);
  return escapeHtml(String(item.reportsTo || ""));
}

/**
 * @param {unknown} item
 * @param {{ companyName: string; logoUrl: string; seniorStaffNurse?: string }} branding
 */
export function protocolItemToHtml(item, branding) {
  if (!item || typeof item !== "object") return "";
  const type = /** @type {{ type?: string }} */ (item).type;
  if (type === "signature") return "";

  const sn = branding.seniorStaffNurse || "";

  switch (type) {
    case "header": {
      const it = /** @type {{
        title?: string;
        subtitle?: string;
        position?: string;
        reportsTo?: string;
      }} */ (item);
      return `<div class="jd-header">
        <h1 class="jd-h1">${escapeHtml(it.title || "Job Description")}</h1>
        ${it.subtitle ? `<p class="jd-sub">${escapeHtml(it.subtitle)}</p>` : ""}
        <div class="jd-meta">
          ${it.position ? `<div><span class="jd-meta-lbl">Position</span><span class="jd-meta-val">${escapeHtml(it.position)}</span></div>` : ""}
          ${it.reportsTo || sn ? `<div><span class="jd-meta-lbl">Reports to</span><span class="jd-meta-val">${reportsToDisplay(it, sn)}</span></div>` : ""}
        </div>
      </div>`;
    }
    case "introduction": {
      const t = /** @type {{ text?: string }} */ (item).text || "";
      return `<p class="jd-intro">${escapeHtml(t)}</p>`;
    }
    case "text_block": {
      const it = /** @type {{ title?: string; text?: string }} */ (item);
      return `<div class="jd-block">
        ${it.title ? `<h2 class="jd-h2">${escapeHtml(it.title)}</h2>` : ""}
        <p class="jd-prose">${escapeHtml(it.text || "")}</p>
      </div>`;
    }
    case "section": {
      const it = /** @type {
        title?: string;
        text?: string;
        list?: string[];
        duties?: string[];
        items?: { number?: string; responsible?: string; text?: string; subItems?: string[] }[];
        generalStatement?: string;
      } */ (item);
      let listHtml = "";
      if (Array.isArray(it.list) && it.list.length > 0) {
        listHtml = `<ul class="jd-ul">${it.list.map((li) => `<li>${escapeHtml(li)}</li>`).join("")}</ul>`;
      }
      let dutiesHtml = "";
      if (Array.isArray(it.duties) && it.duties.length > 0) {
        dutiesHtml = `<ol class="jd-ol">${it.duties.map((d) => `<li>${escapeHtml(d)}</li>`).join("")}</ol>`;
      }
      let itemsHtml = "";
      if (Array.isArray(it.items) && it.items.length > 0) {
        itemsHtml = it.items
          .map((sub) => {
            const num = escapeHtml(String(sub.number ?? ""));
            const resp = sub.responsible
              ? `<span class="jd-resp">[${escapeHtml(sub.responsible)}]</span> `
              : "";
            const subList =
              Array.isArray(sub.subItems) && sub.subItems.length > 0
                ? `<ul class="jd-subul">${sub.subItems.map((si) => `<li>${escapeHtml(si)}</li>`).join("")}</ul>`
                : "";
            return `<div class="jd-numrow"><span class="jd-num">${num}</span><div>${resp}<span>${escapeHtml(sub.text || "")}</span>${subList}</div></div>`;
          })
          .join("");
      }
      const gen = it.generalStatement
        ? `<p class="jd-gen">${escapeHtml(it.generalStatement)}</p>`
        : "";
      return `<section class="jd-section">
        <h2 class="jd-h2">${escapeHtml(it.title || "")}</h2>
        ${it.text ? `<p class="jd-muted">${escapeHtml(it.text)}</p>` : ""}
        ${listHtml}
        ${dutiesHtml}
        ${itemsHtml}
        ${gen}
      </section>`;
    }
    case "protocol_section": {
      const it = /** @type {
        title?: string;
        email?: string;
        subject_line?: string;
        content?: { label?: string; text?: string; basic_supplies?: string[]; specialty_supplies?: string[] }[];
        rules?: string[];
      } */ (item);
      const contentHtml = Array.isArray(it.content)
        ? it.content
            .map((c) => {
              const basics = Array.isArray(c.basic_supplies)
                ? `<p class="jd-tags"><strong>Basic:</strong> ${c.basic_supplies.map((x) => escapeHtml(x)).join(", ")}</p>`
                : "";
              const specs = Array.isArray(c.specialty_supplies)
                ? `<p class="jd-tags"><strong>Specialty:</strong> ${c.specialty_supplies.map((x) => escapeHtml(x)).join(", ")}</p>`
                : "";
              return `<div class="jd-protocard">
              <h5>${escapeHtml(c.label || "")}</h5>
              <p>${escapeHtml(c.text || "")}</p>
              ${basics}
              ${specs}
            </div>`;
            })
            .join("")
        : "";
      const rulesHtml = Array.isArray(it.rules)
        ? `<ul class="jd-rules">${it.rules.map((rule) => `<li>${escapeHtml(rule)}</li>`).join("")}</ul>`
        : "";
      return `<section class="jd-protocol">
        <h2 class="jd-h2-proto">${escapeHtml(it.title || "")}</h2>
        <div class="jd-emailbox">
          <p><strong>Email:</strong> ${escapeHtml(it.email || "")}</p>
          <p><strong>Subject line:</strong> ${escapeHtml(it.subject_line || "")}</p>
        </div>
        <div class="jd-proto-grid">${contentHtml}</div>
        ${rulesHtml}
      </section>`;
    }
    case "supervisory_section": {
      const it = /** @type { title?: string; rules?: string[] } */ (item);
      const rulesHtml = Array.isArray(it.rules)
        ? `<ul class="jd-sup">${it.rules.map((rule) => `<li>${escapeHtml(rule)}</li>`).join("")}</ul>`
        : "";
      return `<section class="jd-supervisory">
        <h2 class="jd-h2-sup">${escapeHtml(it.title || "")}</h2>
        ${rulesHtml}
      </section>`;
    }
    case "exhibit": {
      const it = /** @type {
        title?: string;
        subtitle?: string;
        items?: { number?: string | number; text?: string }[];
      } */ (item);
      const rows = Array.isArray(it.items)
        ? it.items
            .map(
              (ex) =>
                `<div class="jd-exrow"><span class="jd-exnum">${escapeHtml(String(ex.number ?? ""))}.</span><p>${escapeHtml(ex.text || "")}</p></div>`,
            )
            .join("")
        : "";
      return `<section class="jd-exhibit">
        <h2 class="jd-ex-title">${escapeHtml(it.title || "")}</h2>
        <h3 class="jd-ex-sub">${escapeHtml(it.subtitle || "")}</h3>
        ${rows}
      </section>`;
    }
    case "footer": {
      const it = /** @type {{ text?: string; copyright?: string }} */ (item);
      return `<footer class="jd-footer">
        <p>${escapeHtml(it.text || "")}</p>
        <p>${escapeHtml(it.copyright || "")}</p>
      </footer>`;
    }
    default:
      return "";
  }
}

export function signatureBlock(sig) {
  if (sig == null || sig === "") {
    return `<span class="sig-empty">—</span>`;
  }
  const s = String(sig);
  if (s.startsWith("data:")) {
    return `<img class="sig-img" src="${escapeHtml(s)}" alt="" />`;
  }
  return `<span class="sig-text">${escapeHtml(s)}</span>`;
}

/** Shared CSS for protocol-style prints (job description, roles & responsibilities). */
export const PROTOCOL_DOCUMENT_PRINT_STYLES = `
    @page { margin: 0.4in; size: letter; }
    * { box-sizing: border-box; }
    body {
      font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
      font-size: 11px;
      line-height: 1.45;
      color: #111827;
      margin: 0;
      padding: 12px;
    }
    .sheet { max-width: 880px; margin: 0 auto; }
    @media screen {
      html {
        background: linear-gradient(168deg, #cbd5e1 0%, #e2e8f0 42%, #f1f5f9 100%);
        min-height: 100%;
      }
      body {
        padding: 28px 18px 40px;
        background: transparent;
        min-height: 100vh;
      }
      .sheet {
        background: #ffffff;
        padding: 32px 36px 40px;
        border: 1px solid #e2e8f0;
        border-radius: 1px;
        box-shadow:
          0 1px 2px rgba(15, 23, 42, 0.05),
          0 24px 48px -16px rgba(15, 23, 42, 0.22);
      }
    }
    @media print {
      html { background: #fff !important; }
      body {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
        padding: 0 !important;
        margin: 0;
        background: #fff !important;
      }
      .sheet {
        padding: 0 !important;
        border: none !important;
        box-shadow: none !important;
        border-radius: 0 !important;
        background: #fff !important;
      }
    }
    .top {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 16px;
      padding-bottom: 12px;
      border-bottom: 1px solid #e5e7eb;
      margin-bottom: 16px;
    }
    .logo { height: 56px; width: auto; max-width: 200px; object-fit: contain; }
    .title-block { text-align: right; flex: 1; }
    .title-main { font-size: 15px; font-weight: 700; color: #111827; margin: 0 0 4px 0; }
    .title-sub { font-size: 12px; font-weight: 500; color: #2563eb; margin: 0; }
    .jd-header { margin-bottom: 20px; text-align: center; }
    .jd-h1 { font-size: 18px; font-weight: 700; margin: 0 0 8px 0; color: #0f172a; }
    .jd-sub { color: #64748b; margin: 0 0 12px 0; }
    .jd-meta {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
      max-width: 560px;
      margin: 0 auto;
      text-align: left;
    }
    .jd-meta-lbl { display: block; font-size: 9px; font-weight: 600; color: #6b7280; text-transform: uppercase; }
    .jd-meta-val { font-size: 12px; font-weight: 600; color: #111827; }
    .jd-intro {
      font-size: 12px;
      font-style: italic;
      border-left: 4px solid #e5e7eb;
      padding: 8px 12px;
      margin: 0 0 16px 0;
      color: #374151;
    }
    .jd-block { margin-bottom: 18px; }
    .jd-h2 {
      font-size: 13px;
      font-weight: 700;
      color: #1e3a8a;
      border-bottom: 1px solid #e5e7eb;
      padding-bottom: 6px;
      margin: 16px 0 10px 0;
      text-transform: uppercase;
    }
    .jd-prose, .jd-muted { text-align: justify; font-size: 11px; color: #374151; margin: 0 0 10px 0; }
    .jd-muted { font-style: italic; color: #64748b; }
    .jd-section { margin-bottom: 18px; page-break-inside: auto; }
    .jd-ul, .jd-ol { margin: 8px 0; padding-left: 22px; }
    .jd-ul li, .jd-ol li { margin-bottom: 6px; text-align: justify; color: #374151; font-size: 10.5px; }
    .jd-numrow { display: flex; gap: 10px; margin-bottom: 10px; font-size: 10.5px; text-align: justify; }
    .jd-num { font-weight: 700; color: #1d4ed8; flex-shrink: 0; min-width: 28px; }
    .jd-resp { font-weight: 700; color: #0369a1; }
    .jd-subul { margin: 6px 0 0 0; padding-left: 18px; }
    .jd-gen { font-size: 10px; color: #6b7280; text-align: center; margin-top: 10px; }
    .jd-protocol {
      margin: 20px 0;
      padding: 14px;
      border: 1px solid #fcd34d;
      border-radius: 10px;
      background: #fffbeb;
    }
    .jd-h2-proto { font-size: 13px; font-weight: 800; text-align: center; color: #92400e; margin: 0 0 12px 0; }
    .jd-emailbox { background: #fff; padding: 10px; border-radius: 8px; margin-bottom: 12px; font-size: 10px; }
    .jd-proto-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 12px; }
    .jd-protocard { background: rgba(255,255,255,0.85); padding: 10px; border-radius: 8px; font-size: 10px; }
    .jd-protocard h5 { margin: 0 0 6px 0; color: #1e3a8a; font-size: 11px; }
    .jd-tags { margin: 6px 0 0 0; font-size: 9px; color: #334155; }
    .jd-rules { list-style: none; padding: 0; margin: 0; }
    .jd-rules li { padding: 4px 0 4px 16px; position: relative; font-size: 10px; }
    .jd-rules li::before { content: "▶"; position: absolute; left: 0; color: #d97706; font-size: 8px; }
    .jd-supervisory {
      margin: 16px 0;
      padding: 14px;
      border: 1px solid #93c5fd;
      border-radius: 10px;
      background: #eff6ff;
    }
    .jd-h2-sup { font-size: 13px; font-weight: 800; text-align: center; color: #1e3a8a; margin: 0 0 10px 0; }
    .jd-sup { list-style: none; padding: 0; margin: 0; }
    .jd-sup li { display: flex; gap: 8px; margin-bottom: 8px; font-size: 10px; }
    .jd-sup li::before { content: "!"; flex-shrink: 0; width: 18px; height: 18px; border-radius: 9999px; background: #2563eb; color: #fff; font-size: 10px; font-weight: 800; display: flex; align-items: center; justify-content: center; }
    .jd-exhibit { margin: 18px 0; padding: 16px; border: 2px solid #1f2937; border-radius: 8px; }
    .jd-ex-title { text-align: center; font-size: 16px; font-weight: 900; margin: 0 0 8px 0; }
    .jd-ex-sub { text-align: center; font-size: 13px; font-weight: 700; margin: 0 0 14px 0; text-transform: uppercase; }
    .jd-exrow { display: flex; gap: 12px; margin-bottom: 10px; font-size: 10.5px; text-align: justify; }
    .jd-exnum { font-weight: 800; color: #9ca3af; flex-shrink: 0; }
    .jd-footer { margin-top: 24px; padding-top: 12px; border-top: 1px solid #e5e7eb; text-align: center; font-size: 9px; color: #9ca3af; font-style: italic; }
    .cert-box {
      margin-top: 16px;
      padding: 12px 14px;
      background: #eff6ff;
      border: 1px solid #bfdbfe;
      border-radius: 8px;
    }
    .cert-title { font-weight: 700; font-size: 11px; letter-spacing: 0.02em; margin: 0 0 4px 0; }
    .cert-sub { font-size: 9px; color: #6b7280; margin: 0 0 10px 0; text-transform: uppercase; }
    .cert-row {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      background: #fff;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 10px 12px;
      font-size: 11px;
    }
    .chk { width: 14px; height: 14px; margin-top: 2px; flex-shrink: 0; }
    .two-col {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16px;
      margin-top: 20px;
    }
    .sig-card {
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      padding: 12px;
      background: #fafafa;
    }
    .sig-card h3 { font-size: 12px; font-weight: 700; margin: 0 0 10px 0; color: #111827; }
    .sig-label {
      font-size: 9px;
      font-weight: 600;
      letter-spacing: 0.06em;
      color: #6b7280;
      margin: 8px 0 4px 0;
    }
    .sig-line {
      border-bottom: 1px solid #9ca3af;
      min-height: 28px;
      font-size: 12px;
      padding: 4px 0;
    }
    .sig-img { max-height: 48px; max-width: 100%; object-fit: contain; }
    .sig-empty { color: #9ca3af; }
    .field-value {
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 8px 10px;
      min-height: 34px;
      font-size: 11px;
      margin-bottom: 8px;
    }
    .footer-print {
      margin-top: 20px;
      padding-top: 10px;
      border-top: 1px solid #e5e7eb;
      text-align: center;
      font-size: 10px;
      color: #6b7280;
    }
    @media print {
      .jd-proto-grid { grid-template-columns: 1fr 1fr; }
    }
    @media screen and (max-width: 700px) {
      .jd-proto-grid { grid-template-columns: 1fr; }
    }
`;

/**
 * @param {Record<string, unknown>} jd
 * @param {{ companyName: string; logoUrl: string; seniorStaffNurse?: string }} branding
 */
export function buildJobDescriptionPrintHtml(jd, branding) {
  const company = escapeHtml(branding.companyName || "Company");
  const logoUrl = escapeHtml(branding.logoUrl || "");
  const role = normalizeJobDescriptionRole(jd.role);
  const items =
    ROLE_PROTOCOL_ITEMS[role] || RN_PROTOCOL_ITEMS;

  const bodyHtml = items
    .map((it) => protocolItemToHtml(it, branding))
    .join("");

  const displayName = escapeHtml(
    String(jd.applicantName || jd.name || "").trim(),
  );

  const es = jd.employeeSignature;
  const nestedEmpSig =
    es && typeof es === "object"
      ? /** @type {{ signature?: string }} */ (es).signature
      : null;
  const applicantSig =
    jd.applicantSignature || jd.empSignature || nestedEmpSig;
  const applicantSignedAt = escapeHtml(
    formatDate(
      jd.applicantSignedAt ||
        jd.applicantSignatureDate ||
        jd.empSignedAt,
    ),
  );
  const reviewedBySignedBy = escapeHtml(String(jd.reviewedBySignedBy || ""));
  const reviewedBySig = jd.reviewedBySignature || jd.adminSignature;
  const reviewedBySignedAt = escapeHtml(
    formatDate(
      jd.reviewedBySignedAt ||
        jd.adminSignatureDate ||
        jd.reviewedBySignatureDate,
    ),
  );

  const statementAccepted = !!jd.applicantStatementAccepted;
  const statusCompleted =
    String(jd.status || "").toLowerCase() === "completed";
  const showCertChecked = statementAccepted || statusCompleted;

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Job Description (${escapeHtml(role)}) — ${displayName || "Employee"}</title>
  <style>${PROTOCOL_DOCUMENT_PRINT_STYLES}</style>
</head>
<body>
  <div class="sheet">
    <div class="top">
      <img class="logo" src="${logoUrl}" alt="" onerror="this.style.display='none'" />
      <div class="title-block">
        <p class="title-main">Job description &amp; protocols</p>
        <p class="title-sub">Role: ${escapeHtml(role)} · ${company}</p>
      </div>
    </div>

    ${bodyHtml}

    <div class="cert-box">
      <p class="cert-title">Applicant certification</p>
      <p class="cert-sub">Do not sign until you have read the above applicant statement.</p>
      <div class="cert-row">
        <input type="checkbox" class="chk" ${showCertChecked ? "checked" : ""} disabled />
        <span>I certify that I have read, fully understand and accept all terms of the foregoing Applicant Statement. <span style="color:#dc2626">*</span></span>
      </div>
    </div>

    <div class="two-col">
      <div class="sig-card">
        <h3>Employee</h3>
        <div class="sig-label">Name</div>
        <div class="field-value">${displayName || "—"}</div>
        <div class="sig-label">Signature</div>
        <div class="sig-line">${signatureBlock(applicantSig)}</div>
        <div class="sig-label">Date</div>
        <div class="sig-line">${applicantSignedAt || "—"}</div>
      </div>
      <div class="sig-card">
        <h3>Admin / reviewer</h3>
        <div class="sig-label">Name</div>
        <div class="field-value">${reviewedBySignedBy || "—"}</div>
        <div class="sig-label">Signature</div>
        <div class="sig-line">${signatureBlock(reviewedBySig)}</div>
        <div class="sig-label">Date</div>
        <div class="sig-line">${reviewedBySignedAt || "—"}</div>
      </div>
    </div>

    <div class="footer-print">${company}</div>
  </div>
</body>
</html>`;
}
